import { publicApiOrigin } from "./publicUrls";
import { TOS_VERSION } from "./tos";

const API_BASE = `${publicApiOrigin()}/api`;

const ADMIN_HEADER = "X-Admin-Key";

export interface StoreCategory {
  id: number;
  name: string;
  slug: string;
  description: string | null;
}

export interface StoreProduct {
  id: number;
  name: string;
  slug: string;
  description: string;
  image_url: string | null;
  price: number;
  quantity: number;
  category: { id: number; name: string; slug: string } | null;
}

export interface AdminProduct {
  id: number;
  name: string;
  slug: string;
  description: string;
  image_url: string | null;
  price: number;
  quantity: number;
  weight_oz: number;
  length_in: number;
  width_in: number;
  height_in: number;
  category_id: number | null;
  category: { id: number; name: string; slug: string } | null;
  active: boolean;
  stripe_product_id?: string | null;
  stripe_price_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ProductImportRowResult {
  row: number;
  name: string;
  status: string;
  detail?: string | null;
}

export interface ProductImportResponse {
  results: ProductImportRowResult[];
  summary: Record<string, number>;
}

export type CheckoutItem = { product_id: number; quantity: number };

export interface OrderStatusItem {
  name: string;
  quantity: number;
  price_cents: number;
}

export interface OrderStatus {
  order_number: string;
  status: string;
  status_label: string;
  placed_at: string;
  items: OrderStatusItem[];
  shipping_cents: number;
  total_cents: number;
  /** Integer cents refunded so far; 0 when nothing was refunded. */
  refunded_cents: number;
  shipped_at: string | null;
  carrier: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
}

/** Paid order behind a Stripe Checkout session (success page analytics). */
export interface CheckoutSessionSummary {
  order_number: string;
  total_cents: number;
  shipping_cents: number;
  items: { product_id: number; name: string; quantity: number; price_cents: number }[];
}

export interface AdminOrderSummary {
  id: number;
  order_number: string;
  email: string;
  status: string;
  status_label: string;
  /** Integer cents. */
  total: number;
  item_count: number;
  created_at: string;
}

export interface AdminOrderList {
  items: AdminOrderSummary[];
  total: number;
  page: number;
  page_size: number;
}

export interface AdminOrderListParams {
  status?: string;
  created_from?: string;
  created_to?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

export interface ShippingAddress {
  name: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
}

export interface AdminOrderItem {
  product_id: number;
  name: string;
  quantity: number;
  price_at_purchase: number;
  line_total: number;
}

export interface AdminOrderEvent {
  id: number;
  type: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  actor: string;
  created_at: string;
}

export type RefundReason = "requested_by_customer" | "duplicate" | "fraudulent";

export interface AdminOrderRefund {
  id: number;
  stripe_refund_id: string;
  /** Integer cents. */
  amount_cents: number;
  reason: RefundReason | null;
  note: string | null;
  restocked: boolean;
  /** Stripe's refund status, e.g. "succeeded", "pending" or "failed". */
  status: string;
  notified_at: string | null;
  created_at: string;
}

export interface AdminRefundRequest {
  /** Integer cents; omit to refund everything still refundable. */
  amount_cents?: number;
  reason?: RefundReason | null;
  note?: string | null;
  restock: boolean;
  notify_customer: boolean;
}

export interface AdminOrderDetail {
  id: number;
  order_number: string;
  status: string;
  status_label: string;
  allowed_statuses: string[];
  customer_name: string | null;
  email: string;
  shipping_address: ShippingAddress | null;
  items: AdminOrderItem[];
  /** Money fields are integer cents. */
  subtotal: number;
  shipping: number;
  total: number;
  refunded_cents: number;
  /** What a refund could still return; 0 when the order can't be refunded. */
  refundable_cents: number;
  refunds: AdminOrderRefund[];
  stripe_session_id: string | null;
  stripe_payment_intent_id: string | null;
  tos_version: string | null;
  tos_accepted_at: string | null;
  tos_accepted_ip: string | null;
  admin_notes: string | null;
  shipped_at: string | null;
  carrier: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  shipping_notified_at: string | null;
  created_at: string;
  updated_at: string;
  events: AdminOrderEvent[];
}

export interface AdminOrderUpdate {
  email?: string;
  shipping_address?: ShippingAddress;
  admin_notes?: string | null;
}

export type Carrier = "USPS" | "UPS" | "FedEx" | "DHL" | "Other";

export interface AdminShipmentInput {
  carrier: Carrier;
  tracking_number: string;
  /** Only for carrier "Other": an optional https:// tracking link. */
  tracking_url?: string | null;
}

export interface AdminShipOrderRequest extends AdminShipmentInput {
  notify_customer: boolean;
}

export interface AdminUpdateTrackingRequest extends AdminShipmentInput {
  resend_email: boolean;
}

export type OrderStatsRange = "week" | "month" | "quarter" | "ytd";

export interface OrderStatsBucket {
  start: string;
  orders: number;
  /** Integer cents. */
  revenue: number;
  /** Of `orders`, how many were partially or fully refunded. */
  refunded_orders: number;
  /** Integer cents returned to customers on this bucket's orders. */
  refunded: number;
}

export interface OrderStats {
  range: OrderStatsRange;
  granularity: "day" | "week";
  timezone: string;
  start: string;
  end: string;
  buckets: OrderStatsBucket[];
  total_orders: number;
  gross_revenue: number;
  total_refunded_orders: number;
  /** Integer cents. */
  total_refunded: number;
}

export interface ShippingQuoteResponse {
  shipping_cents: number;
  subtotal_cents: number;
  total_cents: number;
  metadata?: Record<string, unknown>;
}

export type CheckoutTosAcceptance = {
  tos_accepted: boolean;
};

async function parseJsonError(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return { detail: response.statusText };
  }
}

export class ApiRequestError extends Error {
  status: number;
  detail: unknown;

  constructor(status: number, detail: unknown) {
    super(typeof detail === "string" ? detail : "Request failed");
    this.status = status;
    this.detail = detail;
  }
}

async function expectJson<T>(response: Response): Promise<T> {
  const data = await parseJsonError(response);
  if (!response.ok) {
    const detail =
      data && typeof data === "object" && "detail" in data
        ? (data as { detail: unknown }).detail
        : data;
    throw new ApiRequestError(response.status, detail);
  }
  return data as T;
}

/** Human-readable message from an API error (FastAPI sends `detail` as a string or a list). */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (!(err instanceof ApiRequestError)) return fallback;
  if (typeof err.detail === "string") return err.detail;
  if (Array.isArray(err.detail)) {
    const messages = err.detail
      .map((d) => {
        if (!d || typeof d !== "object" || !("msg" in d)) return "";
        const loc: unknown[] = "loc" in d && Array.isArray(d.loc) ? d.loc : [];
        const field = loc.filter((part) => part !== "body").pop();
        return field ? `${field}: ${d.msg}` : String(d.msg);
      })
      .filter(Boolean);
    if (messages.length) return messages.join("; ");
  }
  return fallback;
}

export const api = {
  async getProducts(category?: string): Promise<StoreProduct[]> {
    const q = category ? `?category=${encodeURIComponent(category)}` : "";
    const r = await fetch(`${API_BASE}/products${q}`);
    return expectJson<StoreProduct[]>(r);
  },

  async getCategories(): Promise<StoreCategory[]> {
    const r = await fetch(`${API_BASE}/categories`);
    return expectJson<StoreCategory[]>(r);
  },

  async getProduct(slug: string): Promise<StoreProduct> {
    const r = await fetch(`${API_BASE}/products/${encodeURIComponent(slug)}`);
    return expectJson<StoreProduct>(r);
  },

  async quoteShipping(
    items: CheckoutItem[],
    zip: string
  ): Promise<ShippingQuoteResponse> {
    const r = await fetch(`${API_BASE}/shipping/quote`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items, zip }),
    });
    return expectJson<ShippingQuoteResponse>(r);
  },

  async checkout(
    items: CheckoutItem[],
    email: string,
    zip: string,
    tos: CheckoutTosAcceptance
  ): Promise<{ url?: string | null }> {
    const r = await fetch(`${API_BASE}/checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items,
        email,
        zip,
        tos_accepted: tos.tos_accepted,
        tos_version: TOS_VERSION,
      }),
    });
    return expectJson<{ url?: string | null }>(r);
  },

  async getCheckoutSession(sessionId: string): Promise<CheckoutSessionSummary> {
    const r = await fetch(
      `${API_BASE}/checkout/session/${encodeURIComponent(sessionId)}`
    );
    return expectJson<CheckoutSessionSummary>(r);
  },

  async getOrderStatus(email: string, orderNumber: string): Promise<OrderStatus> {
    const r = await fetch(`${API_BASE}/orders/status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, order_number: orderNumber }),
    });
    return expectJson<OrderStatus>(r);
  },

  async adminListProducts(adminKey: string): Promise<AdminProduct[]> {
    const r = await fetch(`${API_BASE}/admin/products`, {
      headers: { [ADMIN_HEADER]: adminKey },
    });
    return expectJson<AdminProduct[]>(r);
  },

  async adminListCategories(adminKey: string): Promise<StoreCategory[]> {
    const r = await fetch(`${API_BASE}/admin/categories`, {
      headers: { [ADMIN_HEADER]: adminKey },
    });
    return expectJson<StoreCategory[]>(r);
  },

  async adminCreateProduct(
    adminKey: string,
    data: Record<string, unknown>
  ): Promise<AdminProduct> {
    const r = await fetch(`${API_BASE}/admin/products`, {
      method: "POST",
      headers: {
        [ADMIN_HEADER]: adminKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return expectJson<AdminProduct>(r);
  },

  async adminUpdateProduct(
    adminKey: string,
    productId: number,
    data: Record<string, unknown>
  ): Promise<AdminProduct> {
    const r = await fetch(`${API_BASE}/admin/products/${productId}`, {
      method: "PUT",
      headers: {
        [ADMIN_HEADER]: adminKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return expectJson<AdminProduct>(r);
  },

  async adminDeleteProduct(adminKey: string, productId: number): Promise<void> {
    const r = await fetch(`${API_BASE}/admin/products/${productId}`, {
      method: "DELETE",
      headers: { [ADMIN_HEADER]: adminKey },
    });
    await expectJson<unknown>(r);
  },

  async adminImportProducts(
    adminKey: string,
    csv: string
  ): Promise<ProductImportResponse> {
    const r = await fetch(`${API_BASE}/admin/products/import`, {
      method: "POST",
      headers: {
        [ADMIN_HEADER]: adminKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ csv }),
    });
    return expectJson<ProductImportResponse>(r);
  },

  async adminCreateCategory(
    adminKey: string,
    data: { name: string; description?: string }
  ): Promise<StoreCategory> {
    const r = await fetch(`${API_BASE}/admin/categories`, {
      method: "POST",
      headers: {
        [ADMIN_HEADER]: adminKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return expectJson<StoreCategory>(r);
  },

  async adminDeleteCategory(adminKey: string, categoryId: number): Promise<void> {
    const r = await fetch(`${API_BASE}/admin/categories/${categoryId}`, {
      method: "DELETE",
      headers: { [ADMIN_HEADER]: adminKey },
    });
    await expectJson<unknown>(r);
  },

  async adminListOrders(
    adminKey: string,
    params: AdminOrderListParams = {}
  ): Promise<AdminOrderList> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "") query.set(key, String(value));
    }
    const qs = query.toString();
    const r = await fetch(`${API_BASE}/admin/orders${qs ? `?${qs}` : ""}`, {
      headers: { [ADMIN_HEADER]: adminKey },
    });
    return expectJson<AdminOrderList>(r);
  },

  async adminOrderStats(
    adminKey: string,
    range: OrderStatsRange
  ): Promise<OrderStats> {
    const r = await fetch(`${API_BASE}/admin/orders/stats?range=${range}`, {
      headers: { [ADMIN_HEADER]: adminKey },
    });
    return expectJson<OrderStats>(r);
  },

  async adminGetOrder(
    adminKey: string,
    orderNumber: string
  ): Promise<AdminOrderDetail> {
    const r = await fetch(
      `${API_BASE}/admin/orders/${encodeURIComponent(orderNumber)}`,
      { headers: { [ADMIN_HEADER]: adminKey } }
    );
    return expectJson<AdminOrderDetail>(r);
  },

  async adminUpdateOrder(
    adminKey: string,
    orderNumber: string,
    data: AdminOrderUpdate
  ): Promise<AdminOrderDetail> {
    const r = await fetch(
      `${API_BASE}/admin/orders/${encodeURIComponent(orderNumber)}`,
      {
        method: "PATCH",
        headers: {
          [ADMIN_HEADER]: adminKey,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }
    );
    return expectJson<AdminOrderDetail>(r);
  },

  async adminSetOrderStatus(
    adminKey: string,
    orderNumber: string,
    status: string
  ): Promise<AdminOrderDetail> {
    const r = await fetch(
      `${API_BASE}/admin/orders/${encodeURIComponent(orderNumber)}/status`,
      {
        method: "POST",
        headers: {
          [ADMIN_HEADER]: adminKey,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      }
    );
    return expectJson<AdminOrderDetail>(r);
  },

  async adminShipOrder(
    adminKey: string,
    orderNumber: string,
    data: AdminShipOrderRequest
  ): Promise<AdminOrderDetail> {
    const r = await fetch(
      `${API_BASE}/admin/orders/${encodeURIComponent(orderNumber)}/ship`,
      {
        method: "POST",
        headers: {
          [ADMIN_HEADER]: adminKey,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }
    );
    return expectJson<AdminOrderDetail>(r);
  },

  async adminUpdateTracking(
    adminKey: string,
    orderNumber: string,
    data: AdminUpdateTrackingRequest
  ): Promise<AdminOrderDetail> {
    const r = await fetch(
      `${API_BASE}/admin/orders/${encodeURIComponent(orderNumber)}/tracking`,
      {
        method: "PUT",
        headers: {
          [ADMIN_HEADER]: adminKey,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }
    );
    return expectJson<AdminOrderDetail>(r);
  },

  async adminRefundOrder(
    adminKey: string,
    orderNumber: string,
    data: AdminRefundRequest
  ): Promise<AdminOrderDetail> {
    const r = await fetch(
      `${API_BASE}/admin/orders/${encodeURIComponent(orderNumber)}/refund`,
      {
        method: "POST",
        headers: {
          [ADMIN_HEADER]: adminKey,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }
    );
    return expectJson<AdminOrderDetail>(r);
  },
};
